import React, { Suspense, lazy, useContext, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { trackPageView, normalizePath } from '../services/analyticsService';
import { trackRecentPage } from '../utils/recentPages';

// Layout components (keep synchronous as they're used on every route)
import MainLayout from '../components/layouts/MainLayout';
import AuthLayout from '../components/layouts/AuthLayout';

// Route guards (keep synchronous)
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

// Branch redirect components for dashboard quick-access links
import { MenuRedirect, TablesRedirect, ReportsRedirect, ExpensesRedirect } from '../components/common/BranchRedirect';
import LoadingAnimation from '../components/common/LoadingAnimation';
import RouteLoading from '../components/landing/RouteLoading';
import { useTranslation } from '../hooks/useTranslation';

import '../components/branches/GlobalBranchList.css';

// Optimized loading component for Suspense (memoized to prevent re-renders)
const LoadingSpinner = React.memo(() => <RouteLoading />);

// Lazy load all route components for code splitting
// Auth components
const SignIn = lazy(() => import('../components/auth/SignIn'));
// Minimal desktop-only sign-in (no signup, no extras)
const ElectronSignIn = lazy(() => import('../components/auth/ElectronSignIn'));
const SignUp = lazy(() => import('../components/auth/SignUp'));
const ForgotPassword = lazy(() => import('../components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../components/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('../components/auth/VerifyEmail'));

// Dashboard components
const OwnerDashboard = lazy(() => import('../components/dashboard/OwnerDashboard'));
const ManagerDashboard = lazy(() => import('../components/dashboard/ManagerDashboard'));
const WaiterDashboard = lazy(() => import('../components/dashboard/WaiterDashboard'));
const SalesAssistantPage = lazy(() => import('../components/assistant/SalesAssistantPage'));

// Role-aware dashboard router
const DashboardRouter = () => {
  const { currentUser } = useContext(AuthContext);
  const role = currentUser?.role;
  if (role === 'waiter' || role === 'chef') return <WaiterDashboard />;
  if (role === 'manager' || role === 'branch_manager' || role === 'restaurant_manager') return <ManagerDashboard />;
  // owner, admin, branch_admin → OwnerDashboard (branch_admin sees scoped version)
  return <OwnerDashboard />;
};

// Restaurant components
const RestaurantList = lazy(() => import('../components/restaurants/RestaurantList'));
const RestaurantDetail = lazy(() => import('../components/restaurants/RestaurantDetail'));
const RestaurantForm = lazy(() => import('../components/restaurants/RestaurantForm'));
const ManageRestaurant = lazy(() => import('../components/restaurants/ManageRestaurant'));
const RestaurantAnalytics = lazy(() => import('../components/restaurants/RestaurantAnalytics'));

// Branch components
const BranchList = lazy(() => import('../components/branches/BranchList'));
const GlobalBranchList = lazy(() => import('../components/branches/GlobalBranchList'));
const BranchDetail = lazy(() => import('../components/branches/BranchDetail'));
const BranchForm = lazy(() => import('../components/branches/BranchForm'));
const BranchAnalytics = lazy(() => import('../components/branches/BranchAnalytics'));
const BranchSettings = lazy(() => import('../components/branches/BranchSettings'));
const WhatsAppAgentPage = lazy(() => import('../components/branches/WhatsAppAgentPage'));
const SalesReport = lazy(() => import('../components/branches/SalesReport'));
const ManagerLogs = lazy(() => import('../components/branches/ManagerLogs'));

// Menu components
const MenuList = lazy(() => import('../components/menu/MenuList'));
const MenuItemForm = lazy(() => import('../components/menu/MenuItemForm'));
const CaptureMenuPage = lazy(() => import('../components/menu/CaptureMenuPage'));

// Table components
const TableManagement = lazy(() => import('../components/tables/TableManagement'));
const TableForm = lazy(() => import('../components/tables/TableForm'));

// Onboarding flow (first-run restaurant setup for new owners)
const OnboardingFlow = lazy(() => import('../components/onboarding/OnboardingFlow'));

// User management components
const UserList = lazy(() => import('../components/users/UserList'));
const UserForm = lazy(() => import('../components/users/UserForm'));

// Order history / my orders pages
const OrdersHistoryPage = lazy(() => import('../components/orders/OrdersHistoryPage'));
const MyOrdersPage = lazy(() => import('../components/orders/MyOrdersPage'));

// Deal form (used for menu deal creation; deal list page removed)
const DealForm = lazy(() => import('../components/deals/DealForm'));

// POS components (heavy components - benefit most from lazy loading)
const POSScreen = lazy(() => import('../components/pos/POSScreen'));
const ResponsivePOSSystem = lazy(() => import('../components/pos/ResponsivePOSSystem'));

// Branch-specific components
const AssignedMenus = lazy(() => import('../components/branch-specific/AssignedMenus'));
const AssignedTables = lazy(() => import('../components/branch-specific/AssignedTables'));

// Expenses (standalone page, reuses DailyExpenseManager)
const ExpensesPage = lazy(() => import('../components/expenses/ExpensesPage'));

// Settings components
const ThemeSelector = lazy(() => import('../components/settings/ThemeSelector'));

// QR Menu components
const QRMenuList = lazy(() => import('../components/qr-menu/QRMenuList'));
const QRMenuEditor = lazy(() => import('../components/qr-menu/QRMenuEditor'));
const QRMenuPreview = lazy(() => import('../components/qr-menu/QRMenuPreview'));
const QRMenuEmbed = lazy(() => import('../components/qr-menu/QRMenuEmbed'));
const PublicQRMenu = lazy(() => import('../components/qr-menu/PublicQRMenu'));
const TabletBoundaryEditor = lazy(() => import('../components/qr-menu/TabletBoundaryEditor'));

// Customer components
const CustomerOrderDisplay = lazy(() => import('../components/customer/CustomerOrderDisplay'));

// Downloads page
const DownloadsPage = lazy(() => import('../components/downloads/DownloadsPage'));

// Invoices page (owner)
const InvoicesPage = lazy(() => import('../components/invoices/InvoicesPage'));

// Admin portal pages
const AdminLayout = lazy(() => import('../components/admin/AdminLayout'));
const AdminPortal = lazy(() => import('../components/admin/AdminPortal'));
const AdminInvoices = lazy(() => import('../components/admin/AdminInvoices'));
const AdminOwners = lazy(() => import('../components/admin/AdminOwners'));
const AdminAlerts = lazy(() => import('../components/admin/AdminAlerts'));
const AdminAnalytics = lazy(() => import('../components/admin/AdminAnalytics'));
const AdminAIUsage = lazy(() => import('../components/admin/AdminAIUsage'));
const AdminPlans = lazy(() => import('../components/admin/AdminPlans'));
const AdminBranchPlans = lazy(() => import('../components/admin/AdminBranchPlans'));
const AdminSales = lazy(() => import('../components/admin/AdminSales'));

// Blocked account page
const BlockedPage = lazy(() => import('../components/common/BlockedPage'));

// Error pages
const NotFound = lazy(() => import('../common/NotFound'));

const LandingPage = lazy(() => import('../components/landing/LandingPage'));
const PricingRoutePage = lazy(() => import('../components/landing/PricingRoutePage'));
const TermsOfService = lazy(() => import('../components/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('../components/legal/PrivacyPolicy'));

function HomeRoute() {
  const { currentUser, isLoading } = useContext(AuthContext);
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0D0D0D 0%, #212529 100%)',
      }}>
        <LoadingAnimation fullPage message={t('common.loading')} />
      </div>
    );
  }
  if (currentUser) {
    // Desktop POS: skip the dashboard and open the POS screen directly.
    if (window.electronAPI?.isElectron && currentUser.branchId) {
      return <Navigate to={`/pos/${currentUser.branchId}`} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  // In the desktop app skip the marketing landing page — go straight to sign-in
  if (window.electronAPI?.isElectron) {
    return <Navigate to="/signin" replace />;
  }
  return <LandingPage />;
}

function PublicLandingLayout() {
  return (
    <LandingPage>
      <Outlet />
    </LandingPage>
  );
}

const RestaurantDetailWithKey = () => {
  const location = useLocation();
  return <RestaurantDetail key={location.pathname} />;
};

// Fires a page view on every route change. Landing ('/') and the auth pages
// track themselves (they carry isLanding / conversion semantics), and
// customer-facing QR/display routes are excluded so product analytics only
// reflect owner/staff usage of the management app.
const TRACK_SKIP_EXACT = new Set(['/', '/signin', '/signup', '/forgot-password']);
const TRACK_SKIP_PREFIX = ['/qr-menu', '/customer-display', '/reset-password', '/tablet-boundary-editor', '/blocked'];

function RouteTracker() {
  const location = useLocation();
  const lastTracked = useRef(null);

  useEffect(() => {
    const raw = location.pathname || '/';
    if (TRACK_SKIP_EXACT.has(raw)) return;
    if (TRACK_SKIP_PREFIX.some((p) => raw.startsWith(p))) return;

    const page = normalizePath(raw);
    // Avoid duplicate fires for the same normalized page (e.g. re-renders)
    if (lastTracked.current === page) return;
    lastTracked.current = page;
    trackPageView(page);
    trackRecentPage(raw);
  }, [location.pathname]);

  return null;
}

const AppRoutes = () => {
  return (
    <AnimatePresence mode="wait">
      <RouteTracker />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
        {/* Landing for unauthenticated users; redirect to dashboard when authenticated */}
        <Route path="/" element={<HomeRoute />} />

        {/* Public pages using landing header/footer */}
        <Route element={<PublicLandingLayout />}>
          <Route path="/pricing" element={<PricingRoutePage />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/policy" element={<PrivacyPolicy />} />
        </Route>

        {/* Auth Routes */}
        <Route element={<PublicRoute />}>
          {/* Desktop app: minimal sign-in screen with no layout wrapper */}
          {window.electronAPI?.isElectron ? (
            <Route path="/signin" element={<ElectronSignIn />} />
          ) : (
            <Route element={<AuthLayout />}>
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
            </Route>
          )}
        </Route>
        
        {/* Protected Routes */}
        <Route element={<PrivateRoute />}>
          {/* QR embed previews — outside MainLayout so iframes show template only */}
          <Route path="/qr-menu/:id/embed/:templateId" element={<QRMenuEmbed />} />

          <Route element={<MainLayout />}>
            {/* First-run setup flow — renders the owner dashboard blurred behind it */}
            <Route path="/onboarding" element={<OnboardingFlow />} />

            {/* Dashboards */}
            <Route path="/dashboard" element={<DashboardRouter />} />
            <Route path="/owner/dashboard" element={<OwnerDashboard />} />
            <Route path="/manager/dashboard" element={<ManagerDashboard />} />
            <Route path="/waiter/dashboard" element={<WaiterDashboard />} />
            <Route path="/assistant" element={<SalesAssistantPage />} />
            
            {/* Dashboard quick-access redirects (redirect to branch-specific routes) */}
            <Route path="/menu" element={<MenuRedirect />} />
            <Route path="/tables" element={<TablesRedirect />} />
            <Route path="/reports" element={<ReportsRedirect />} />
            <Route path="/expenses" element={<ExpensesRedirect />} />
            
            {/* Restaurant Routes */}
            <Route path="/manage-restaurant" element={<ManageRestaurant />} />
            <Route path="/restaurants" element={<RestaurantList />} />
            <Route path="/restaurants/new" element={<RestaurantForm />} />
            <Route path="/restaurants/:id" element={<RestaurantDetailWithKey />} />
            <Route path="/restaurants/:id/edit" element={<RestaurantForm />} />
            
            {/* Restaurant Menu Routes */}
            <Route path="/restaurants/:restaurantId/menu" element={<MenuList />} />
            <Route path="/restaurants/:restaurantId/menu/capture" element={<CaptureMenuPage />} />
            <Route path="/restaurants/:restaurantId/menu/new" element={<MenuItemForm />} />
            <Route path="/restaurants/:restaurantId/menu/deal/new" element={<DealForm />} />
            <Route path="/restaurants/:restaurantId/menu/:menuItemId/edit" element={<MenuItemForm />} />
            
            {/* Restaurant Analytics Routes */}
            <Route path="/restaurants/:id/analytics" element={<RestaurantAnalytics />} />
            
            {/* Global Branch Routes */}
            <Route path="/branches" element={<GlobalBranchList />} />
            <Route path="/branches/:id" element={<BranchDetail />} />
            <Route path="/branches/:id/edit" element={<BranchForm />} />
            <Route path="/branches/:id/analytics" element={<BranchAnalytics />} />
            <Route path="/branches/:id/settings" element={<BranchSettings />} />
            <Route path="/branches/:id/whatsapp-agent" element={<WhatsAppAgentPage />} />
            <Route path="/branches/:id/sales-report" element={<SalesReport />} />
            <Route path="/branches/:id/manager-logs" element={<ManagerLogs />} />
            <Route path="/branches/:branchId/expenses" element={<ExpensesPage />} />
            
            {/* Branch Routes - nested under restaurants */}
            <Route path="/restaurants/:restaurantId/branches" element={<BranchList />} />
            <Route path="/restaurants/:restaurantId/branches/new" element={<BranchForm />} />
            
            {/* Branch-specific routes */}
            <Route path="/assigned-menus" element={<AssignedMenus />} />
            <Route path="/assigned-tables" element={<AssignedTables />} />
            
            {/* Branch Menu Routes */}
            <Route path="/branches/:branchId/menu" element={<MenuList />} />
            <Route path="/branches/:branchId/menu/capture" element={<CaptureMenuPage />} />
            <Route path="/branches/:branchId/menu/new" element={<MenuItemForm />} />
            <Route path="/branches/:branchId/menu/deal/new" element={<DealForm />} />
            <Route path="/branches/:branchId/menu/:menuItemId/edit" element={<MenuItemForm />} />
            
            {/* Menu Routes - nested under branches */}
            <Route path="/branches/:branchId/menu" element={<MenuList />} />
            <Route path="/menu-items/:id/edit" element={<MenuItemForm />} />
            
            {/* Table Routes - nested under branches */}
            <Route path="/branches/:branchId/tables" element={<TableManagement />} />
            <Route path="/branches/:branchId/tables/new" element={<TableForm />} />
            <Route path="/tables/:id/edit" element={<TableForm />} />
            <Route path="/deals/:id/edit" element={<DealForm />} />
            
            {/* POS Routes */}
            <Route path="/pos" element={<POSScreen />} />
            <Route path="/pos/:branchId" element={<POSScreen />} />
            <Route path="/pos-responsive" element={<ResponsivePOSSystem />} />
            <Route path="/pos-responsive/:branchId" element={<ResponsivePOSSystem />} />
            
            {/* Staff/User Routes */}
            <Route path="/users" element={<UserList />} />
            <Route path="/users/new" element={<UserForm />} />
            <Route path="/users/:id/edit" element={<UserForm />} />

            {/* Orders History (admin) / My Orders (waiter/user) */}
            <Route path="/orders-history" element={<OrdersHistoryPage />} />
            <Route path="/my-orders" element={<MyOrdersPage />} />

            {/* Settings Routes */}
            <Route path="/settings/themes" element={<ThemeSelector />} />
            
            {/* QR Menu Routes */}
            <Route path="/qr-menu" element={<QRMenuList />} />
            <Route path="/qr-menu/:id/edit" element={<QRMenuEditor />} />
            <Route path="/qr-menu/:id/preview" element={<QRMenuPreview />} />

            {/* Downloads */}
            <Route path="/downloads" element={<DownloadsPage />} />

            {/* Invoices (owner) */}
            <Route path="/invoices" element={<InvoicesPage />} />
          </Route>
        </Route>

        {/* Admin Portal — platform_admin only, outside MainLayout */}
        <Route element={<PrivateRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminPortal />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="owners" element={<AdminOwners />} />
            <Route path="alerts" element={<AdminAlerts />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="ai-usage" element={<AdminAIUsage />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="branch-plans" element={<AdminBranchPlans />} />
            <Route path="sales" element={<AdminSales />} />
          </Route>
        </Route>

        {/* Blocked account page — public route */}
        <Route path="/blocked" element={<BlockedPage />} />
        
        {/* Public QR Menu Routes (no auth required) - more specific paths first */}
        <Route path="/qr-menu/:url/order/:orderId" element={<PublicQRMenu />} />
        <Route path="/qr-menu/:url/cart" element={<PublicQRMenu />} />
        <Route path="/qr-menu/:url/bill" element={<PublicQRMenu />} />
        <Route path="/qr-menu/:url/item/:itemId" element={<PublicQRMenu />} />
        <Route path="/qr-menu/:url/dish/:itemId" element={<PublicQRMenu />} />
        <Route path="/qr-menu/:url" element={<PublicQRMenu />} />
        
        {/* Customer Order Display (no auth required - public display) */}
        <Route path="/customer-display/:branchId" element={<CustomerOrderDisplay />} />
        
        {/* Temporary Tablet Boundary Editor (no auth required for easy access) */}
        <Route path="/tablet-boundary-editor" element={<TabletBoundaryEditor />} />
        
        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

export default AppRoutes;